import { useApolloClient, useLazyQuery } from '@apollo/client'
import ActionButton from '@deltek/specification-client/core/Buttons/ActionButton'
import { NavPromptMessageProps } from '@deltek/specification-client/core/Forms/NavPrompt'
import NavPromptDialogContainer from '@deltek/specification-client/core/Forms/NavPromptDialogContainer'
import { ListingMatch } from '@deltek/specification-client/core/Models/listingSearch'
import { ContentLibrary } from '@deltek/specification-client/core/Models/product'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import {
    sortList,
    unescapeElementDelimiter,
} from '@deltek/specification-client/core/Widgets/ProductListing/productsUtils'
import DialogComponent from '@deltek/specification-client/core/Widgets/ProjectSearch/DialogComponent'
import { Backdrop } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import Select from '@material-ui/core/Select'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { SearchFacetInput } from 'api/graphQL/types/product.type'
import { ExportListingsCount } from 'bpm/api/graphQL/queries/productSearch.queries'
import { ProductSearchResponse } from 'bpm/api/graphQL/types/product.types'
import clsx from 'clsx'
import { Options as CSVOptions, ExportToCsv } from 'export-to-csv'
import React, { useEffect, useState } from 'react'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import { buildProductExportQuery } from './productExportQuery'

/** Rows requested per `listingSearch` page. */
const MAX_ROWS_PER_PAGE = 5000

/**
 * Pages kept in flight at once. Browsers allow six concurrent connections per
 * origin over HTTP/1.1, and every page costs the backend two Elasticsearch
 * round trips (the match query plus a facet aggregation the export never
 * reads), so four leaves headroom for the rest of the app.
 */
const MAX_CONCURRENT_PAGES = 4

/** Attempts per page before the export is abandoned. */
const MAX_PAGE_ATTEMPTS = 3

/**
 * Fetches `pageCount` pages with at most `MAX_CONCURRENT_PAGES` in flight and
 * returns their rows concatenated in page order regardless of the order they
 * complete in, so the export keeps the order the backend paged them in.
 *
 * Rejects as soon as any page gives up: once a short export is a file on the
 * user's disk it is indistinguishable from a complete one.
 */
const fetchPagesInParallel = async (
    pageCount: number,
    fetchPage: (pageIndex: number) => Promise<Partial<ListingMatch>[]>
): Promise<Partial<ListingMatch>[]> => {
    const pages: Partial<ListingMatch>[][] = new Array(pageCount)
    let nextPage = 0
    let failed = false

    // Workers pull from a shared cursor rather than owning a fixed slice, so a
    // slow page does not idle the others.
    const consumePages = async (): Promise<void> => {
        while (!failed && nextPage < pageCount) {
            const pageIndex = nextPage++
            try {
                pages[pageIndex] = await fetchPage(pageIndex)
            } catch (error) {
                failed = true
                throw error
            }
        }
    }

    await Promise.all(
        Array.from(
            { length: Math.min(MAX_CONCURRENT_PAGES, pageCount) },
            consumePages
        )
    )

    return pages.flat()
}

const useStyles = makeStyles((theme: Theme) => ({
    exportButton: {
        ...theme.typography.body2,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    form: {
        height: '100%',
        width: '50%',
    },
    inputLegend: {
        '& legend': {
            width: '60px !important',
        },
    },
    inputLabel: {
        backgroundColor: 'white',
        padding: '0 0.5rem',
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.secondary.light,
    },
    notchedOutline: {
        top: 0,
    },
}))

interface DialogActionsProps {
    handleCancel: VoidFunction
    handleExport: VoidFunction
    disabled: boolean
}

export const DialogActions: React.FC<DialogActionsProps> = ({
    handleCancel,
    handleExport,
    disabled,
}) => {
    const classes = useStyles()
    return (
        <>
            <ActionButton
                color="secondary"
                onClick={(_: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                    handleCancel()
                }
            >
                <Typography className={classes.exportButton}>Cancel</Typography>
            </ActionButton>
            <ActionButton
                color="primary"
                disabled={disabled}
                onClick={(_: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                    handleExport()
                }
            >
                <Typography className={classes.exportButton}>Export</Typography>
            </ActionButton>
        </>
    )
}

interface ResultsExportProps {
    open: boolean
    handleClose: VoidFunction
    tableGrouping: any
    selectedColumns: any
    columnWidths: any
    orderBy: string
    manufacturerProductSearchInput: any
    totalCount: number
    assignedCompany: string
    isAdminAccess: boolean
    normalizeFacets: (
        facets: Partial<SearchFacetInput>[]
    ) => Partial<SearchFacetInput>[]
}

const ResultsExport: React.FC<ResultsExportProps> = ({
    open,
    handleClose,
    tableGrouping,
    selectedColumns,
    columnWidths,
    orderBy,
    manufacturerProductSearchInput,
    totalCount,
    assignedCompany,
    isAdminAccess,
    normalizeFacets,
}) => {
    const typeOptions = [
        { display: 'CSV', value: 'csv' },
        { display: 'XLSX', value: 'xlsx' },
    ]

    const columns =
        [...tableGrouping, ...selectedColumns]?.filter(
            (col) => col.id !== 'hasProductDetailNotes'
        ) ?? []
    const header = columns.map((col) => col.title) ?? []
    const colIds = columns.map((col) => col.id) ?? []

    const [totalRows, setTotalRows] = useState(totalCount)
    const [type, setType] = React.useState<string>('')
    const [productList, setProductList] = React.useState<any[]>([])
    const [getAllProducts, setGetAllProducts] = React.useState<boolean>(false)
    const [isExported, setIsExported] = React.useState<boolean>(true)
    const prevManufacturerProductSearchInput = usePrevious(
        manufacturerProductSearchInput
    )
    const { showGlobalSnackbar } = useGlobalSnackbar()

    // Select only the columns being exported instead of the full listing card,
    // which drops images/videos/catalogs/docs from the payload.
    const colIdsKey = colIds.join(',')
    // Keyed on colIdsKey, not colIds: colIds is a fresh array on every render
    // (line 134), so depending on it directly would rebuild the document every
    // render and clear productList mid-export.
    const exportQuery = React.useMemo(
        () => buildProductExportQuery(colIds, orderBy),
        [colIdsKey, orderBy]
    )
    const prevExportQuery = usePrevious(exportQuery)

    // `useLazyQuery` cannot page concurrently -- invoking it again while a
    // request is in flight replaces the observable rather than adding one, so
    // the pages have to go through the client directly.
    const apolloClient = useApolloClient()

    const buildSearchInput = React.useCallback(
        (skip: number) => ({
            ...manufacturerProductSearchInput,
            skip,
            top: MAX_ROWS_PER_PAGE,
            facets: !!isAdminAccess
                ? manufacturerProductSearchInput.facets
                : normalizeFacets([
                      ...manufacturerProductSearchInput.facets,
                      {
                          name: 'displayNameWithFirmName',
                          values: [unescapeElementDelimiter(assignedCompany)],
                      },
                  ]),
        }),
        [
            manufacturerProductSearchInput,
            isAdminAccess,
            assignedCompany,
            normalizeFacets,
        ]
    )

    const fetchExportPage = React.useCallback(
        async (pageIndex: number): Promise<Partial<ListingMatch>[]> => {
            for (let attempt = 1; ; attempt++) {
                try {
                    const { data } = await apolloClient.query<
                        ProductSearchResponse
                    >({
                        query: exportQuery,
                        variables: {
                            manufacturerProductSearchInput: buildSearchInput(
                                pageIndex * MAX_ROWS_PER_PAGE
                            ),
                        },
                        fetchPolicy: 'no-cache',
                    })
                    // Each page is sorted on its own, matching how the serial
                    // fetch sorted every response before appending it.
                    return sortList(
                        [...(data?.listingSearch?.matches ?? [])],
                        orderBy
                    )
                } catch (error) {
                    if (attempt >= MAX_PAGE_ATTEMPTS) throw error
                    showGlobalSnackbar({
                        variables: {
                            message: 'Error on product search. Retrying...',
                            variant: 'error',
                            id: `ProductListingsPage_ProductAutosuggestError`,
                        },
                    })
                }
            }
        },
        [
            apolloClient,
            exportQuery,
            buildSearchInput,
            orderBy,
            showGlobalSnackbar,
        ]
    )

    const [getListingCount] = useLazyQuery<ProductSearchResponse>(
        ExportListingsCount,
        {
            displayName: 'ExportProductSearchCount',
            fetchPolicy: 'no-cache',
            nextFetchPolicy: 'no-cache',
            onError: () => {
                // just set to 0 if errors
            },
            onCompleted: (data) => {
                setTotalRows(data?.listingSearch?.count ?? 0)
                NavPromptDialogContainer(
                    JSON.stringify({
                        title: 'Export Product Management Table',
                        preferredMessage: `There are ${
                            data?.listingSearch?.count ?? 0
                        } rows to export. Are you sure you want to export?`,
                        buttonColor: '#2D9CDB',
                        confirmText: 'Yes',
                        cancelText: 'No',
                    } as NavPromptMessageProps),
                    (callbackState: boolean) => {
                        if (!callbackState) return
                        if (totalCount) setGetAllProducts(true)
                        else
                            showGlobalSnackbar({
                                variables: {
                                    message: 'No data to export',
                                    variant: 'error',
                                    id: `ProductListingsPage_ExportError`,
                                },
                            })
                    }
                )
            },
        }
    )

    const handleValueChange = React.useCallback((newValue: string) => {
        setType(newValue)
    }, [])

    const handleChange = React.useCallback(
        (event: React.ChangeEvent<{ value: unknown }>) => {
            handleValueChange(event.target.value as string)
        },
        []
    )

    // Takes the rows to write rather than reading `productList`, so the export
    // can run in the same tick the pages resolve instead of waiting a render
    // for the state to settle.
    const handleExport = React.useCallback(
        (rows: Partial<ListingMatch>[]) => {
            const data = rows.map((row) =>
                colIds.map((keyName: keyof ListingMatch) => {
                    if (
                        Array.isArray(row['categories']) &&
                        keyName === 'categories'
                    ) {
                        return `${row[keyName]
                            ?.join(', ')
                            .replaceAll("'", '')
                            .trim()}`
                    } else if (keyName === 'isActive') {
                        return row[keyName] ? 'Active' : 'Inactive'
                    } else if (keyName === 'displayNameWithFirmName') {
                        const actualString = row[keyName]
                            ?.split('%|%')
                            ?.map((val: string | null) =>
                                val?.replaceAll("'", '')
                            )
                        const formattedString =
                            actualString?.[0] !== 'null'
                                ? `${actualString?.[0]} (${actualString?.[1]})`
                                : `${actualString?.[1]}`
                        return formattedString?.trim()
                    } else if (keyName === 'productName') {
                        return !!row[keyName] ? row[keyName] : 'No product name'
                    } else if (keyName === 'specAgentProductTypeId') {
                        const actualString = String(row[keyName])
                        return actualString
                    } else if (keyName === 'contentLibrary')
                        return ContentLibrary[
                            row[keyName] as keyof typeof ContentLibrary
                        ]
                    return row[keyName] ?? ''
                })
            )
            const fileName = 'ProductCardManagementTable - Specpoint'
            switch (type) {
                case 'csv': {
                    const options: CSVOptions = {
                        filename: fileName,
                        fieldSeparator: ',',
                        showLabels: true,
                        headers: header,
                    }
                    const csvExporter = new ExportToCsv(options)
                    csvExporter.generateCsv(data)
                    setIsExported(true)
                    handleClose()
                    break
                }
                case 'xlsx': {
                    import('xlsx').then((xlsx) => {
                        const worksheet = xlsx.utils.json_to_sheet(data)
                        if (!worksheet['!cols']) worksheet['!cols'] = []
                        for (let i = 0; i < colIds.length; i++) {
                            if (!worksheet['!cols'][i])
                                worksheet['!cols'][i] = { wpx: 300 }
                            worksheet['!cols'][i].wpx =
                                columnWidths[colIds[i]] ?? 300
                        }
                        const column = colIds.findIndex(
                            (col) => col === 'productName'
                        )
                        if (column !== -1) {
                            const columnLetter = xlsx.utils.encode_col(column)
                            for (let i = 0; i < (rows?.length ?? 0); i++) {
                                worksheet[
                                    `${columnLetter}${i + 2}`
                                ].f = `HYPERLINK("${
                                    window.location.origin
                                }/bpm/products/product/listing/${
                                    rows[i].listingId ?? ''
                                }","${
                                    // Optional-chained because a listing can
                                    // come back without a product name -- the
                                    // row mapping above substitutes a
                                    // placeholder for that case, so the
                                    // hyperlink label must tolerate it too.
                                    rows[i].productName?.replaceAll(
                                        '"',
                                        '""'
                                    ) ?? ''
                                }")`
                            }
                        }
                        xlsx.utils.sheet_add_json(worksheet, [header], {
                            origin: 'A1',
                            skipHeader: true,
                        })
                        const newWorkbook = xlsx.utils.book_new()
                        xlsx.utils.book_append_sheet(
                            newWorkbook,
                            worksheet,
                            'Sheet1'
                        )
                        xlsx.writeFile(newWorkbook, `${fileName}.xlsx`)
                        setIsExported(true)
                        handleClose()
                        return
                    })
                    break
                }
                default:
                    break
            }
        },
        [type, header, colIds, columnWidths, handleClose]
    )

    // Fetches every page for the export at once instead of walking them one
    // response at a time. The row count is known before the first request, so
    // the offsets can all be computed up front rather than derived from how
    // many rows have arrived so far.
    //
    // Deliberately keyed on `getAllProducts` alone: the closure captures the
    // query, filters and sort as of the render that started the export, which
    // is exactly the snapshot the export should use. Re-running on those
    // dependencies would restart an in-flight export.
    useEffect(() => {
        if (!getAllProducts) return

        const pageCount = Math.ceil(totalRows / MAX_ROWS_PER_PAGE)
        if (!pageCount) {
            setGetAllProducts(false)
            return
        }

        let cancelled = false

        fetchPagesInParallel(pageCount, fetchExportPage)
            .then((rows) => {
                if (cancelled) return
                setProductList(rows)
                setGetAllProducts(false)
                setIsExported(false)
                handleExport(rows)
            })
            .catch(() => {
                if (cancelled) return
                setGetAllProducts(false)
                showGlobalSnackbar({
                    variables: {
                        message:
                            'Error on product search. Export cancelled, please try again.',
                        variant: 'error',
                        id: `ProductListingsPage_ExportError`,
                    },
                })
            })

        return () => {
            cancelled = true
        }
    }, [getAllProducts])

    //used to empty out productList when filters change
    React.useMemo(() => {
        if (
            prevManufacturerProductSearchInput !==
            manufacturerProductSearchInput
        ) {
            setProductList([])
        }
    }, [manufacturerProductSearchInput])

    //used to empty out productList when the selected columns change.
    //Rows already fetched only carry the previously selected fields, and
    //productList survives a completed export, so reusing them would emit blanks
    //for any newly added column.
    useEffect(() => {
        if (prevExportQuery && prevExportQuery !== exportQuery) {
            setProductList([])
        }
    }, [exportQuery])

    const handleExportClick = React.useCallback(() => {
        if (!isAdminAccess) {
            getListingCount({
                variables: {
                    manufacturerProductSearchInput: {
                        ...manufacturerProductSearchInput,
                        skip: productList.length,
                        top: MAX_ROWS_PER_PAGE,
                        facets: normalizeFacets([
                            ...manufacturerProductSearchInput.facets,
                            {
                                name: 'displayNameWithFirmName',
                                values: [
                                    unescapeElementDelimiter(assignedCompany),
                                ],
                            },
                        ]),
                    },
                },
            })
        } else {
            NavPromptDialogContainer(
                JSON.stringify({
                    title: 'Export Product Management Table',
                    preferredMessage: `There are ${totalRows} rows to export. Are you sure you want to export?`,
                    buttonColor: '#2D9CDB',
                    confirmText: 'Yes',
                    cancelText: 'No',
                } as NavPromptMessageProps),
                (callbackState: boolean) => {
                    if (!callbackState) return
                    if (totalRows) setGetAllProducts(true)
                    else
                        showGlobalSnackbar({
                            variables: {
                                message: 'No data to export',
                                variant: 'error',
                                id: `ProductListingsPage_ExportError`,
                            },
                        })
                }
            )
        }
    }, [totalRows, isAdminAccess, productList, assignedCompany])

    useEffect(() => {
        if (isAdminAccess) {
            setTotalRows(totalCount)
        }
    }, [totalCount])

    const classes = useStyles()

    return (
        <DialogComponent
            dialogId="tableExport"
            dialogName="Export"
            open={open}
            handleClose={handleClose}
            defaultDialogHeight={200}
            defaultDialogWidth={440}
            dialogActions={
                <DialogActions
                    handleCancel={handleClose}
                    handleExport={handleExportClick}
                    disabled={type === ''}
                />
            }
        >
            <FormControl
                variant="outlined"
                className={classes.form}
                data-testid="dvTableExportType"
            >
                <InputLabel className={classes.inputLabel}>
                    Export As
                </InputLabel>
                <Select
                    onChange={handleChange}
                    value={type}
                    input={
                        <OutlinedInput
                            labelWidth={70}
                            classes={{
                                notchedOutline: clsx(
                                    type && classes.inputLegend,
                                    classes.notchedOutline
                                ),
                            }}
                        />
                    }
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {typeOptions.map((menuItem) => (
                        <MenuItem key={menuItem.value} value={menuItem.value}>
                            {menuItem.display}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Backdrop
                className={classes.backdrop}
                open={getAllProducts || !isExported}
            >
                {<CircularProgress color="inherit" />}
            </Backdrop>
        </DialogComponent>
    )
}

export default ResultsExport
